import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EMPTY, Subject, catchError, filter, switchMap, take, takeUntil } from 'rxjs';
import { Chart, registerables } from 'chart.js';
import { AuthService } from '../../../../services/auth.service';
import { AcquisitionFunnelReportDto, MarketingAcquisitionService } from '../../../../services/marketing-acquisition.service';
import { ChartWrapperComponent } from '../../../charts/shared/chart-wrapper/chart-wrapper.component';
import { EntityChartDataDto } from '../../../../models/chart.models';
import type { AcquisitionDateRange } from '../acquisition.component';

// Don't rely on another component having registered Chart.js first (re-registering is a no-op).
Chart.register(...registerables);

/**
 * V1 — acquisition funnel by channel: leads whose first touch fell in the range, what they did
 * within the attribution window, and what it cost.
 */
@Component({
  selector: 'app-acquisition-funnel',
  standalone: true,
  imports: [CommonModule, ChartWrapperComponent],
  templateUrl: './acquisition-funnel.component.html'
})
export class AcquisitionFunnelComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) range!: AcquisitionDateRange;

  loading = false;
  errorMessage = '';
  report: AcquisitionFunnelReportDto | null = null;
  leadsChart: EntityChartDataDto | null = null;
  revenueChart: EntityChartDataDto | null = null;

  private readonly load$ = new Subject<AcquisitionDateRange>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private acquisitionService: MarketingAcquisitionService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.load$
      .pipe(
        switchMap(range => {
          this.loading = true;
          this.errorMessage = '';
          return this.acquisitionService.getFunnel(range.from, range.to).pipe(
            catchError(error => {
              this.onError(error);
              return EMPTY;
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(report => this.onReport(report));

    this.authService.getMsalInitializedObservable()
      .pipe(filter(initialized => initialized), take(1), takeUntil(this.destroy$))
      .subscribe(() => this.load$.next(this.range));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['range'] && !changes['range'].firstChange) {
      this.load$.next(this.range);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private onReport(report: AcquisitionFunnelReportDto): void {
    this.report = report;
    this.leadsChart = {
      entityName: 'Leads',
      totalCount: report.totals.leads,
      dataPoints: report.rows.map(r => ({ label: r.label, value: r.leads }))
    };
    this.revenueChart = {
      entityName: 'Revenue (₹)',
      totalCount: report.totals.revenue,
      dataPoints: report.rows.map(r => ({ label: r.label, value: r.revenue }))
    };
    this.loading = false;
  }

  private onError(error: { status?: number; error?: { error?: string } }): void {
    this.loading = false;
    this.report = null;
    if (error.status === 400 && error.error?.error) {
      this.errorMessage = error.error.error;
    } else if (error.status === 403) {
      this.errorMessage = 'Access denied. You do not have permission to view marketing reports.';
    } else {
      this.errorMessage = 'Failed to load the acquisition funnel. Please try again.';
    }
  }
}
